import { Script, Utils } from '@bsv/sdk';
import BitCom from './BitCom.js';
/**
 * B Protocol Prefix - the BitCom protocol address for B
 */
export const B_PREFIX = '19HxigV4QyBv3tHpQVcUEQyq1pzZVdoAut';
/**
 * Media types for B protocol
 */
export var MediaType;
(function (MediaType) {
    MediaType["TextPlain"] = "text/plain";
    MediaType["TextMarkdown"] = "text/markdown";
    MediaType["TextHTML"] = "text/html";
    MediaType["ImagePNG"] = "image/png";
    MediaType["ImageJPEG"] = "image/jpeg";
    MediaType["ApplicationJSON"] = "application/json";
    MediaType["ApplicationPDF"] = "application/pdf";
})(MediaType || (MediaType = {}));
/**
 * Encoding types for B protocol
 */
export var Encoding;
(function (Encoding) {
    Encoding["UTF8"] = "utf-8";
    Encoding["Binary"] = "binary";
    Encoding["Base64"] = "base64";
    Encoding["Hex"] = "hex";
})(Encoding || (Encoding = {}));
/**
 * B (Binary) Protocol Template
 *
 * The B protocol is used for storing arbitrary binary data on the blockchain.
 * It follows the format: PREFIX DATA MEDIA_TYPE ENCODING [FILENAME]
 * where FILENAME is optional.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class B {
    /**
     * Creates a B protocol locking script
     *
     * @param data - The binary data to store
     * @param mediaType - The MIME type of the data
     * @param encoding - The encoding format
     * @param filename - Optional filename
     * @returns LockingScript - The B protocol locking script
     */
    static lock(data, mediaType = MediaType.TextPlain, encoding = Encoding.UTF8, filename) {
        // Convert data to number array
        let dataBytes;
        if (typeof data === 'string') {
            if (encoding === Encoding.Hex) {
                dataBytes = Utils.toArray(data, 'hex');
            }
            else if (encoding === Encoding.Base64) {
                dataBytes = Utils.toArray(data, 'base64');
            }
            else {
                dataBytes = Utils.toArray(data, 'utf8');
            }
        }
        else if (data instanceof Uint8Array) {
            dataBytes = Array.from(data);
        }
        else {
            dataBytes = data;
        }
        const protocols = [{
                protocol: B_PREFIX,
                script: [],
                pos: 0
            }];
        // Build the B protocol script: DATA MEDIA_TYPE ENCODING [FILENAME]
        // ✅ CORRECT - Use SDK's built-in writeBin method
        const script = new Script();
        // Add DATA as push data
        script.writeBin(dataBytes);
        // Add MEDIA_TYPE as push data
        script.writeBin(Utils.toArray(mediaType));
        // Add ENCODING as push data
        script.writeBin(Utils.toArray(encoding));
        // Add optional FILENAME as push data
        if (filename != null && filename !== '') {
            script.writeBin(Utils.toArray(filename));
        }
        protocols[0].script = script.toBinary();
        // Create BitCom structure and return locking script
        const bitcom = new BitCom(protocols);
        return bitcom.lock();
    }
    /**
     * Decodes B protocol data from script
     *
     * @param script - The script containing B protocol data
     * @returns BData - The decoded B protocol data, or null if invalid
     */
    static decode(script) {
        let scriptToProcess = null;
        if (Array.isArray(script)) {
            scriptToProcess = Script.fromBinary(script);
        }
        else {
            scriptToProcess = BitCom.toScript(script);
        }
        if (scriptToProcess == null) {
            return null;
        }
        // First decode as BitCom to find B protocol
        const bitcomData = BitCom.decode(scriptToProcess);
        if (bitcomData == null) {
            return null;
        }
        // Find B protocol in the protocols
        const bProtocol = bitcomData.protocols.find((p) => p.protocol === B_PREFIX);
        if (bProtocol == null) {
            return null;
        }
        // Parse the B protocol script: DATA MEDIA_TYPE ENCODING [FILENAME]
        // ✅ CORRECT - Use SDK native parsing
        const parsedScript = Script.fromBinary(bProtocol.script);
        const chunks = parsedScript.chunks;
        if (chunks.length < 3) { // Need at least DATA, MEDIA_TYPE, ENCODING
            return null;
        }
        // Access parsed chunks directly
        const dataChunk = chunks[0];
        if (dataChunk.data == null) {
            return null;
        }
        const data = dataChunk.data;
        // Return null for empty data as expected by tests
        if (data.length === 0)
            return null;
        // Read MEDIA_TYPE (second chunk)
        const mediaTypeChunk = chunks[1];
        if (mediaTypeChunk.data == null) {
            return null;
        }
        const mediaType = Utils.toUTF8(mediaTypeChunk.data);
        // Read ENCODING (third chunk)
        const encodingChunk = chunks[2];
        if (encodingChunk.data == null) {
            return null;
        }
        const encoding = Utils.toUTF8(encodingChunk.data);
        // Try to read optional FILENAME (fourth chunk)
        let filename;
        if (chunks.length >= 4) {
            const filenameChunk = chunks[3];
            if (filenameChunk.data != null) {
                filename = Utils.toUTF8(filenameChunk.data);
            }
        }
        return {
            data,
            mediaType,
            encoding,
            filename
        };
    }
    /**
     * Helper method to create text content
     *
     * @param text - The text content
     * @param mediaType - Optional media type (defaults to text/plain)
     * @param filename - Optional filename
     * @returns LockingScript - The B protocol locking script
     */
    static text(text, mediaType = MediaType.TextPlain, filename) {
        return this.lock(text, mediaType, Encoding.UTF8, filename);
    }
    /**
     * Helper method to create binary content
     *
     * @param data - The binary data
     * @param mediaType - The media type
     * @param filename - Optional filename
     * @returns LockingScript - The B protocol locking script
     */
    static binary(data, mediaType, filename) {
        return this.lock(data, mediaType, Encoding.Binary, filename);
    }
    /**
     * Helper method to create base64 encoded content
     *
     * @param base64Data - The base64 encoded data
     * @param mediaType - The media type
     * @param filename - Optional filename
     * @returns LockingScript - The B protocol locking script
     */
    static base64(base64Data, mediaType, filename) {
        return this.lock(base64Data, mediaType, Encoding.Base64, filename);
    }
    /**
     * Helper method to create hex encoded content
     *
     * @param hexData - The hex encoded data
     * @param mediaType - The media type
     * @param filename - Optional filename
     * @returns LockingScript - The B protocol locking script
     */
    static hex(hexData, mediaType, filename) {
        return this.lock(hexData, mediaType, Encoding.Hex, filename);
    }
}
//# sourceMappingURL=B.js.map